import { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import initApi from '@/api/initApiV2';

import {
  ProjectRisksApi,
  type ProjectRisksApiAddRisksToProjectRequest,
  type ProjectRisksApiAddRiskThreatsToRiskRequest,
  type ProjectRisksApiDeleteRiskRequest,
  type ProjectRisksApiDeleteRiskThreatRequest,
  type ProjectRisksApiGetLatestRiskThreatQuantificationRequest,
  type ProjectRisksApiGetRiskThreatQuantificationDeltaRequest,
  type ProjectRisksApiGetProjectRisksQuantificationsTimeseriesRequest,
  type ProjectRisksApiGetOrganizationProjectsQuantificationsTimeseriesRequest,
  type ProjectRisksApiGetRiskThreatQuantificationRequest,
  type ProjectRisksApiGetRiskQuantificationsTimeseriesRequest,
  type ProjectRisksApiGetRiskThreatQuantificationsRequest,
  type ProjectRisksApiGetRiskThreatQuantificationsTimeseriesRequest,
  type ProjectRisksApiGetRiskRequest,
  type ProjectRisksApiGetRisksExpandedRequest,
  type ProjectRisksApiGetRisksOverviewRequest,
  type ProjectRisksApiGetRisksRequest,
  type ProjectRisksApiGetRiskThreatRequest,
  type ProjectRisksApiGetRiskThreatsExpandedRequest,
  type ProjectRisksApiGetSimulatedRiskLimitRequest,
  type ProjectRisksApiQuantifyRiskThreatRequest,
  type ProjectRisksApiUpdateRiskRequest,
  type Risk,
  type RiskExpandedPaginated,
  type RiskPaginated,
  type RisksOverview,
  type RiskThreat,
  type RiskThreatExpandedPaginated,
  type ProjectRiskQuantificationTimeseriesResult,
  type OrganizationProjectQuantificationTimeseriesResult,
  type RiskQuantificationTimeseriesResult,
  type RiskThreatQuantification,
  type RiskThreatQuantificationDeltaSchema,
  type RiskThreatQuantificationPaginated,
  type RiskThreatQuantificationTimeseriesResult,
} from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';
import type { RequestConfig } from '@/api/Request.ts';

export const useProjectRisks = defineStore('project-risks', () => {
  const projectRisksOverview = ref<RisksOverview | null>(null);
  const projectRisks = ref<RiskPaginated | null>(null);
  const projectRisksExpanded = ref<RiskExpandedPaginated | null>(null);
  const currentRisk = ref<Risk | null>(null);
  const riskThreats = ref<RiskThreat[]>([]);
  const riskThreatsExpanded = ref<RiskThreatExpandedPaginated | null>(null);
  const currentThreat = ref<RiskThreat | null>(null);
  const currentQuantification = ref<RiskThreatQuantification | null>(null);
  const quantificationHistory = ref<RiskThreatQuantificationPaginated | null>(null);

  // Latest request wins.
  let risksOverviewRequestId = 0;

  const getProjectRisksOverview = async (params: ProjectRisksApiGetRisksOverviewRequest) => {
    const requestId = ++risksOverviewRequestId;
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRisksOverview(params);

      if (!response || requestId !== risksOverviewRequestId) return;

      projectRisksOverview.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectRisks = async (
    params: ProjectRisksApiGetRisksRequest,
    options?: RequestConfig,
    reassign = true,
  ) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRisks(params, options);

      if (!response) return;

      if (reassign) {
        projectRisks.value = response.data;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectRisksExpanded = async (
    params: ProjectRisksApiGetRisksExpandedRequest,
    options?: RequestConfig,
    reassign = true,
  ) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRisksExpanded(params, options);

      if (!response) return;

      if (reassign) {
        projectRisksExpanded.value = response.data;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const addRiskToProject = async (params: ProjectRisksApiAddRisksToProjectRequest) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.addRisksToProject(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectRisk = async (params: ProjectRisksApiGetRiskRequest) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRisk(params);

      if (!response) return;

      currentRisk.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateProjectRisk = async (params: ProjectRisksApiUpdateRiskRequest) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.updateRisk(params);

      if (!response) return;

      currentRisk.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const addRiskThreatsToRisk = async (params: ProjectRisksApiAddRiskThreatsToRiskRequest) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.addRiskThreatsToRisk(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const deleteRiskThreat = async (params: ProjectRisksApiDeleteRiskThreatRequest) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.deleteRiskThreat(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRiskThreat = async (params: ProjectRisksApiGetRiskThreatRequest) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRiskThreat(params);

      if (!response) return;

      currentThreat.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRiskThreats = async (params: ProjectRisksApiGetRiskRequest, options?: RequestConfig) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRiskThreats(params, options);

      if (!response) return;

      riskThreats.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRiskThreatsExpanded = async (
    params: ProjectRisksApiGetRiskThreatsExpandedRequest,
    options?: RequestConfig,
  ) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRiskThreatsExpanded(params, options);

      if (!response) return;

      riskThreatsExpanded.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const quantifyRiskThreat = async (params: ProjectRisksApiQuantifyRiskThreatRequest) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.quantifyRiskThreat(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getLatestRiskThreatQuantification = async (
    params: ProjectRisksApiGetLatestRiskThreatQuantificationRequest,
    options?: RequestConfig,
  ) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getLatestRiskThreatQuantification(params, options);

      if (!response) return;

      currentQuantification.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRiskThreatQuantification = async (
    params: ProjectRisksApiGetRiskThreatQuantificationRequest,
    reassign = true,
    options?: RequestConfig,
  ) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRiskThreatQuantification(params, options);

      if (!response) return;

      if (reassign) {
        currentQuantification.value = response.data;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRiskThreatQuantifications = async (
    params: ProjectRisksApiGetRiskThreatQuantificationsRequest,
    options?: RequestConfig,
    reassign = true,
  ) => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRiskThreatQuantifications(params, options);

      if (!response) return;

      if (reassign) {
        quantificationHistory.value = response.data;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRiskThreatQuantificationsTimeseries = async (
    params: ProjectRisksApiGetRiskThreatQuantificationsTimeseriesRequest,
  ): Promise<RiskThreatQuantificationTimeseriesResult | undefined> => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRiskThreatQuantificationsTimeseries(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRiskThreatQuantificationDelta = async (
    params: ProjectRisksApiGetRiskThreatQuantificationDeltaRequest,
  ): Promise<RiskThreatQuantificationDeltaSchema | undefined> => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRiskThreatQuantificationDelta(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRiskQuantificationsTimeseries = async (
    params: ProjectRisksApiGetRiskQuantificationsTimeseriesRequest,
  ): Promise<RiskQuantificationTimeseriesResult | undefined> => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getRiskQuantificationsTimeseries(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectRisksQuantificationsTimeseries = async (
    params: ProjectRisksApiGetProjectRisksQuantificationsTimeseriesRequest,
  ): Promise<ProjectRiskQuantificationTimeseriesResult | undefined> => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response = await projectRisksApi?.getProjectRisksQuantificationsTimeseries(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationProjectsQuantificationsTimeseries = async (
    params: ProjectRisksApiGetOrganizationProjectsQuantificationsTimeseriesRequest,
  ): Promise<OrganizationProjectQuantificationTimeseriesResult | undefined> => {
    const projectRisksApi = await initApi(ProjectRisksApi);

    try {
      const response =
        await projectRisksApi?.getOrganizationProjectsQuantificationsTimeseries(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const deleteRiskFromProject = async (params: ProjectRisksApiDeleteRiskRequest) => {
    const projectRisksApi = await initApi(ProjectRisksApi);
    try {
      const response = await projectRisksApi?.deleteRisk(params);
      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getSimulatedRiskLimit = async (params: ProjectRisksApiGetSimulatedRiskLimitRequest) => {
    const projectRisksApi = await initApi(ProjectRisksApi);
    try {
      const response = await projectRisksApi?.getSimulatedRiskLimit(params);
      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const resetRisksOverview = () => {
    risksOverviewRequestId += 1;
    projectRisksOverview.value = null;
  };

  const resetRisks = () => {
    projectRisks.value = null;
    projectRisksExpanded.value = null;
  };

  const resetRisk = () => {
    currentRisk.value = null;
    riskThreats.value = [];
    riskThreatsExpanded.value = null;
  };

  const resetQuantification = () => {
    currentThreat.value = null;
    currentQuantification.value = null;
    quantificationHistory.value = null;
  };

  return {
    currentRisk,
    riskThreats,
    projectRisks,
    projectRisksExpanded,
    riskThreatsExpanded,
    currentThreat,
    currentQuantification,
    quantificationHistory,
    projectRisksOverview,

    addRiskToProject,
    getProjectRisks,
    getRiskThreat,
    getProjectRisksOverview,
    getProjectRisksExpanded,
    getProjectRisk,
    updateProjectRisk,
    getRiskThreats,
    getRiskThreatsExpanded,
    addRiskThreatsToRisk,
    deleteRiskThreat,
    quantifyRiskThreat,
    getLatestRiskThreatQuantification,
    getRiskThreatQuantification,
    getRiskThreatQuantifications,
    getRiskThreatQuantificationsTimeseries,
    getRiskThreatQuantificationDelta,
    getRiskQuantificationsTimeseries,
    getProjectRisksQuantificationsTimeseries,
    getOrganizationProjectsQuantificationsTimeseries,
    deleteRiskFromProject,
    getSimulatedRiskLimit,
    resetRisksOverview,
    resetRisks,
    resetRisk,
    resetQuantification,
  };
});
